import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useInfiniteShopProducts, type ShopProductsParams } from '../hooks/useCatalog';
import { usePublicCategories, useSettings } from '../hooks/usePublicContent';
import { Container } from '../components/ui/Container';
import { Eyebrow } from '../components/ui/Eyebrow';
import { ProductCard } from '../components/ProductCard';
import { QuickViewModal } from '../components/QuickViewModal';
import type { CatalogProduct } from '../lib/catalogTypes';

type SortKey = string;

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'newest',           label: 'Newest' },
  { value: 'featured',         label: 'Featured' },
  { value: 'best_selling',     label: 'Best Selling' },
  { value: 'highest_rated',    label: 'Highest Rated' },
  { value: 'most_reviewed',    label: 'Most Reviewed' },
  { value: 'views',            label: 'Most Popular' },
  { value: 'price_asc',        label: 'Price: Low to High' },
  { value: 'price_desc',       label: 'Price: High to Low' },
  { value: 'alpha_asc',        label: 'A – Z' },
  { value: 'alpha_desc',       label: 'Z – A' },
  { value: 'recently_updated', label: 'Recently Updated' },
  { value: 'in_stock',         label: 'In Stock First' },
  { value: 'manual',           label: 'Curated' },
];

const PER_PAGE = 24;

export function ShopAllPage() {
  const { data: settings } = useSettings();
  const { data: categories = [] } = usePublicCategories();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quickViewProduct, setQuickViewProduct] = useState<CatalogProduct | null>(null);

  const search   = searchParams.get('search') ?? '';
  const category = searchParams.get('category') ?? '';
  const sort     = (searchParams.get('sort') as SortKey) ?? 'newest';
  const minPrice = searchParams.get('min_price') ?? '';
  const maxPrice = searchParams.get('max_price') ?? '';

  const [priceMin, setPriceMin] = useState(minPrice);
  const [priceMax, setPriceMax] = useState(maxPrice);
  const [searchInput, setSearchInput] = useState(search);

  const shopTitle = settings?.shop_all?.title ?? 'Shop All';

  const params: Omit<ShopProductsParams, 'page'> = { sort: sort || 'newest', per_page: PER_PAGE };
  if (search)   params.search    = search;
  if (category) params.category  = category;
  if (minPrice) params.min_price = Number(minPrice);
  if (maxPrice) params.max_price = Number(maxPrice);

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteShopProducts(params);

  const products = data?.pages.flatMap(p => p.data) ?? [];
  const total    = data?.pages[0]?.meta.total;

  // Infinite scroll: observe a sentinel div placed below the product grid
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const setParam = useCallback((key: string, value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value); else next.delete(key);
      return next;
    });
  }, [setSearchParams]);

  // Keep local inputs in sync when URL params change externally (e.g. browser back)
  useEffect(() => { setSearchInput(search); }, [search]);
  useEffect(() => { setPriceMin(minPrice); }, [minPrice]);
  useEffect(() => { setPriceMax(maxPrice); }, [maxPrice]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setParam('search', searchInput.trim());
  };

  const clearAll = () => setSearchParams({});
  const hasFilters = !!(search || category || minPrice || maxPrice);

  // Dim grid only while re-fetching due to a filter change, not while loading more pages
  const isDimmed = isFetching && !isFetchingNextPage && !isLoading;

  return (
    <div>
      {/* Page header */}
      <header className="border-b border-stone-light bg-paper pb-10 pt-14 md:pb-12 md:pt-20">
        <Container>
          <nav className="mb-4 flex items-center gap-1.5 text-xs text-stone">
            <Link to="/" className="hover:text-ink">Home</Link>
            <span>/</span>
            <span className="text-ink">{shopTitle}</span>
          </nav>
          <Eyebrow>Collection</Eyebrow>
          <h1 className="mt-5 max-w-2xl font-serif text-4xl font-light leading-[1.05] text-ink md:text-5xl">
            {shopTitle}
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-stone">
            Browse our full range of art prints — each set in a solid-wood frame, printed to archival standard.
          </p>
        </Container>
      </header>

      <Container className="py-10 md:py-14">
        <div className="flex flex-col gap-8 md:flex-row md:gap-10">
          {/* ── Sidebar filters ─────────────────────────────────── */}
          <aside className="w-full md:w-56 shrink-0">
            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="mb-6">
              <label className="block text-xs font-semibold uppercase tracking-widest text-stone mb-2">Search</label>
              <div className="flex gap-1">
                <input
                  type="search"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="Search products…"
                  className="w-full border border-stone-light bg-transparent px-3 py-2 text-sm text-ink placeholder:text-stone/60 focus:border-ink focus:outline-none"
                />
                <button type="submit" className="border border-stone-light px-2 text-stone hover:text-ink focus:outline-none" aria-label="Search">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </form>

            {/* Category filter */}
            {categories.length > 0 && (
              <div className="mb-6">
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-stone">Category</p>
                <ul className="space-y-1">
                  <li>
                    <button
                      onClick={() => setParam('category', '')}
                      className={`text-sm w-full text-left py-0.5 transition-colors ${!category ? 'font-semibold text-ink' : 'text-stone hover:text-ink'}`}
                    >
                      All
                    </button>
                  </li>
                  {categories.map(c => (
                    <li key={c.id}>
                      <button
                        onClick={() => setParam('category', c.slug)}
                        className={`text-sm w-full text-left py-0.5 transition-colors ${category === c.slug ? 'font-semibold text-ink' : 'text-stone hover:text-ink'}`}
                      >
                        {c.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Price filter */}
            <div className="mb-6">
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-stone">Price</p>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  value={priceMin}
                  onChange={e => setPriceMin(e.target.value)}
                  onBlur={() => setParam('min_price', priceMin)}
                  onKeyDown={e => e.key === 'Enter' && setParam('min_price', priceMin)}
                  placeholder="Min"
                  className="w-full border border-stone-light bg-transparent px-2 py-1.5 text-sm text-ink placeholder:text-stone/60 focus:border-ink focus:outline-none"
                />
                <input
                  type="number"
                  min="0"
                  value={priceMax}
                  onChange={e => setPriceMax(e.target.value)}
                  onBlur={() => setParam('max_price', priceMax)}
                  onKeyDown={e => e.key === 'Enter' && setParam('max_price', priceMax)}
                  placeholder="Max"
                  className="w-full border border-stone-light bg-transparent px-2 py-1.5 text-sm text-ink placeholder:text-stone/60 focus:border-ink focus:outline-none"
                />
              </div>
            </div>

            {hasFilters && (
              <button onClick={clearAll} className="text-xs text-stone underline underline-offset-2 hover:text-ink">
                Clear all filters
              </button>
            )}
          </aside>

          {/* ── Product grid ─────────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-stone-light pb-4">
              <p className="text-sm text-stone">
                {isLoading ? 'Loading…' : total != null ? `${total} product${total !== 1 ? 's' : ''}` : ''}
              </p>
              <select
                value={sort || 'newest'}
                onChange={e => setParam('sort', e.target.value)}
                className="border border-stone-light bg-transparent px-3 py-1.5 text-sm text-ink focus:border-ink focus:outline-none"
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Initial skeleton */}
            {isLoading && (
              <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-[4/5] animate-pulse bg-stone-light/50" />
                ))}
              </div>
            )}

            {!isLoading && (
              <div className={isDimmed ? 'opacity-60 pointer-events-none' : ''}>
                {products.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 text-center">
                    <p className="text-stone mb-3">No products found.</p>
                    {hasFilters && (
                      <button onClick={clearAll} className="text-sm underline underline-offset-2 text-ink hover:opacity-70">
                        Clear filters
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-3 lg:grid-cols-4">
                      {products.map(product => (
                        <ProductCard key={product.id} product={product} onQuickView={setQuickViewProduct} />
                      ))}
                    </div>

                    {/* Sentinel — IntersectionObserver triggers next page fetch when visible */}
                    <div ref={sentinelRef} className="mt-8" />

                    {/* Spinner while fetching next page */}
                    {isFetchingNextPage && (
                      <div className="mt-4 flex justify-center py-6">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-stone-light border-t-ink" />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Container>

      <QuickViewModal product={quickViewProduct} onClose={() => setQuickViewProduct(null)} />
    </div>
  );
}
